/**
 * +grid: The Waystone Grid (docs/design/waystone-grid.md): one shared passive grid of ~320 stars.
 * `GRID` is built once (deterministic); `validateGrid` holds the structural rules the tests pin.
 */
import { GRID_IDLE_TEXT, GRID_KEYSTONES, GRID_MIDDLE_MULT, GRID_NOTABLES, GRID_STAT_TEXT, GRID_STAT_VALUE, GRID_IDLE_VALUE, slotModText } from './data';
import { buildGrid } from './layout';
import type { GridDef, GridEffect, GridNode, GridNodeId, GridRing } from './types';

export * from './types';
export * from './data';
export { buildGrid } from './layout';

let cached: GridDef | null = null;
/** The grid (built on first use, then shared; treat as read-only). */
export function getGrid(): GridDef {
  return (cached ??= buildGrid());
}

export function gridNode(id: GridNodeId): GridNode | undefined {
  return getGrid().byId.get(id);
}

/** The gate a class starts from (undefined for classes without one, e.g. the legacy M+ kits). */
export function gridGateOf(classId: string): GridNodeId | undefined {
  const base = classId.includes('@') ? classId.slice(0, classId.indexOf('@')) : classId;
  return getGrid().gates[base];
}

export const notableDef = (id: string) => GRID_NOTABLES.find((x) => x.id === id);
export const keystoneDef = (id: string) => GRID_KEYSTONES.find((x) => x.id === id);

/** Player-facing lines for an effect (tooltips, search). */
export function gridEffectText(e: GridEffect): string[] {
  switch (e.kind) {
    case 'stat':
      return [GRID_STAT_TEXT[e.stat](e.value)];
    case 'idle':
      return [GRID_IDLE_TEXT[e.stat](e.value)];
    case 'slot':
      return [slotModText(e.sel, e.mod)];
    case 'notable':
      return [notableDef(e.id)?.text ?? e.id];
    case 'keystone': {
      const k = keystoneDef(e.id);
      return k ? [k.text, k.cost] : [e.id];
    }
  }
}

export function gridNodeText(n: GridNode): string[] {
  return n.effects.flatMap(gridEffectText);
}

/** A node's power budget in "inner minor" units (1 = one inner minor node's worth). */
export function nodeBudget(n: GridNode): number {
  let b = 0;
  for (const e of n.effects) {
    if (e.kind === 'stat') b += e.value / GRID_STAT_VALUE[e.stat];
    else if (e.kind === 'idle') b += e.value / GRID_IDLE_VALUE[e.stat];
  }
  return Math.round(b * 1000) / 1000;
}

/** Highest minor budget allowed per ring (middle and outer minors give ×GRID_MIDDLE_MULT). */
export const GRID_RING_MINOR_BUDGET: Readonly<Record<GridRing, number>> = { hub: 1, inner: 1, border: 1, middle: GRID_MIDDLE_MULT, outer: GRID_MIDDLE_MULT };

/** Structural problems (empty = valid): links, connectivity, gates, locks, budgets, effect references. */
export function validateGrid(g: GridDef = getGrid()): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const n of g.nodes) {
    if (ids.has(n.id)) out.push(`duplicate id ${n.id}`);
    ids.add(n.id);
    if (!n.name) out.push(`${n.id}: no name`);
    if (!n.links.length) out.push(`${n.id}: orphan`);
    for (const l of n.links) {
      const o = g.byId.get(l);
      if (!o) out.push(`${n.id}: link to missing ${l}`);
      else if (!o.links.includes(n.id)) out.push(`${n.id}–${l}: one-way link`);
      if (l === n.id) out.push(`${n.id}: self link`);
    }
    for (const e of n.effects) {
      if (e.kind === 'notable' && !notableDef(e.id)) out.push(`${n.id}: unknown notable ${e.id}`);
      if (e.kind === 'keystone' && !keystoneDef(e.id)) out.push(`${n.id}: unknown keystone ${e.id}`);
    }
    if (n.type === 'minor' && nodeBudget(n) > GRID_RING_MINOR_BUDGET[n.ring] + 1e-9) out.push(`${n.id}: minor budget ${nodeBudget(n)} over ${GRID_RING_MINOR_BUDGET[n.ring]}`);
    if (n.type === 'utility' && nodeBudget(n) > 1 + 1e-9) out.push(`${n.id}: utility budget ${nodeBudget(n)}`);
    if ((n.type === 'gate' || n.type === 'lock' || n.type === 'dormant') && n.effects.length) out.push(`${n.id}: ${n.type} with effects`);
    if (n.ring === 'middle' && n.type !== 'lock' && !n.lockedBy) out.push(`${n.id}: middle ring without a lock`);
  }
  // Connected (dormant stars included: they hang off real nodes).
  const first = g.nodes[0];
  if (first) {
    const seen = reach(g, [first.id], () => true);
    for (const n of g.nodes) if (!seen.has(n.id)) out.push(`${n.id}: not connected`);
  }
  // Every gate reaches its own region and the whole inner ring without crossing a lock or dormant star.
  const open = (n: GridNode): boolean => n.type !== 'lock' && n.type !== 'dormant';
  for (const [cls, gate] of Object.entries(g.gates)) {
    const seen = reach(g, [gate], open);
    for (const n of g.nodes) if ((n.ring === 'inner' || n.ring === 'border' || n.ring === 'hub') && !seen.has(n.id)) out.push(`${cls}: gate cannot reach ${n.id}`);
    // Locks guard the middle ring: with locks closed nothing beyond them is reachable.
    for (const n of g.nodes) if ((n.ring === 'middle' && n.type !== 'lock') || n.type === 'keystone') if (seen.has(n.id)) out.push(`${cls}: ${n.id} reachable without a lock`);
  }
  // Each pocket is reachable through its own lock.
  for (const n of g.nodes) {
    if (!n.lockedBy) continue;
    const lock = g.byId.get(n.lockedBy);
    if (!lock || lock.type !== 'lock') out.push(`${n.id}: lockedBy ${n.lockedBy} is not a lock`);
  }
  return out;
}

/** Breadth-first reach from `start` through nodes `pass` accepts (start nodes always count). */
function reach(g: GridDef, start: readonly string[], pass: (n: GridNode) => boolean): Set<string> {
  const seen = new Set<string>(start);
  const q = [...start];
  while (q.length) {
    const id = q.shift()!;
    for (const l of g.byId.get(id)?.links ?? []) {
      if (seen.has(l)) continue;
      const n = g.byId.get(l);
      if (!n || !pass(n)) continue;
      seen.add(l);
      q.push(l);
    }
  }
  return seen;
}
